import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { BusinessProcessResponse } from '@/domain/models/project.js';
import type { ListAiModels } from '@/domain/use-cases/ai-models.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import { useAiModels } from '@/presentation/hooks/use-ai-models.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useProjectForm } from '@/presentation/hooks/use-project-form.js';
import { BusinessProcessDialog } from '@/presentation/pages/projects/project-form/components/dialogs/business-process-dialog.js';
import { DeleteConfirmDialog } from '@/presentation/pages/projects/project-form/components/dialogs/delete-confirm-dialog.js';
import { UploadDocumentDialog } from '@/presentation/pages/projects/project-form/components/dialogs/upload-document-dialog.js';
import { ProjectFormTabs } from '@/presentation/pages/projects/project-form/components/project-form-tabs.js';
import { DataTab } from '@/presentation/pages/projects/project-form/components/tabs/data-tab.js';
import { DocumentsTab } from '@/presentation/pages/projects/project-form/components/tabs/documents-tab.js';
import { LinksTab } from '@/presentation/pages/projects/project-form/components/tabs/links-tab.js';
import { ProcessesTab } from '@/presentation/pages/projects/project-form/components/tabs/processes-tab.js';
import { SlasTab } from '@/presentation/pages/projects/project-form/components/tabs/slas-tab.js';
import { type ProjectFormTabKey } from '@/presentation/pages/projects/project-form/utils/get-visible-tabs.js';
import { DOC_CATEGORIES } from '@/shared/constants/document-categories.js';
import { gradientButtonSx } from '@/shared/utils/brand-gradient.js';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    controller: ProjectController;
    clientController: ClientController;
    listAiModelsUseCase: ListAiModels;
    mode: 'create' | 'edit';
    projectId?: string;
}

export function ProjectFormDialog({ open, onClose, onSaved, controller, clientController, listAiModelsUseCase, mode, projectId }: Props) {
    const { translate } = useApp();
    const { catalog: aiModelsCatalog, isLoading: aiModelsLoading } = useAiModels(listAiModelsUseCase);
    const isEdit = mode === 'edit';

    const [activeTab, setActiveTab] = useState<ProjectFormTabKey>('dados');
    const [processModal, setProcessModal] = useState<{ mode: 'add' | 'edit'; data?: BusinessProcessResponse } | null>(null);
    const [processDeleteId, setProcessDeleteId] = useState<string | null>(null);
    const [docDeleteId, setDocDeleteId] = useState<string | null>(null);
    const [uploadModalCategory, setUploadModalCategory] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setActiveTab('dados');
        }
    }, [open]);

    const { loading, saving, form, clients, selectedClient, errors, projectStatus, statusSaving, processes, documents, setField, handleClientChange, handleSave, toggleProjectStatus, saveBusinessProcess, toggleProcessActive, deleteBusinessProcess, uploadDocument, downloadDocument, deleteDocument } = useProjectForm({ controller, clientController, mode, projectId, open, onSaved, onClose });

    const openProcessModal = (m: 'add' | 'edit', data?: BusinessProcessResponse) => {
        setProcessModal({ mode: m, data });
    };

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (saving) {
                    return;
                }
                onClose();
            }}
            disableEscapeKeyDown={saving}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                            {isEdit ? translate('projects.edit') : translate('projects.create')}
                        </Typography>
                        {isEdit && form.abbreviation && <Chip label={form.abbreviation} size="small" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', fontSize: 12 }} />}
                    </Stack>
                </Box>
                <IconButton
                    onClick={() => {
                        if (!saving) {
                            onClose();
                        }
                    }}
                    size="small"
                >
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <ProjectFormTabs value={activeTab} isEdit={isEdit} onChange={setActiveTab} />

            <DialogContent sx={{ p: 3 }}>
                {loading ? (
                    <Typography>{translate('common.loading')}</Typography>
                ) : (
                    <>
                        {activeTab === 'dados' && (
                            <DataTab
                                form={form}
                                errors={errors}
                                onField={setField}
                                isEdit={isEdit}
                                clients={clients}
                                selectedClient={selectedClient}
                                projectStatus={projectStatus}
                                statusSaving={statusSaving}
                                onClientChange={handleClientChange}
                                onToggleStatus={toggleProjectStatus}
                            />
                        )}

                        {activeTab === 'slas' && (
                            <SlasTab form={form} onField={setField} selectedClient={selectedClient} aiModelsCatalog={aiModelsCatalog} aiModelsLoading={aiModelsLoading} />
                        )}

                        {activeTab === 'processos' && isEdit && (
                            <ProcessesTab
                                processes={processes}
                                onAdd={() => openProcessModal('add')}
                                onEdit={(process) => openProcessModal('edit', process)}
                                onToggleActive={toggleProcessActive}
                                onDelete={(processId) => setProcessDeleteId(processId)}
                            />
                        )}

                        {activeTab === 'documentos' && isEdit && (
                            <DocumentsTab documents={documents} onDownload={downloadDocument} onDelete={(docId) => setDocDeleteId(docId)} onUpload={(category) => setUploadModalCategory(category)} />
                        )}

                        {activeTab === 'links' && <LinksTab value={form.reportsUrl} onChange={(v) => setField('reportsUrl', v)} error={errors.reportsUrl} disabled={saving} />}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
                    {translate('common.cancel')}
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || loading} disableElevation sx={gradientButtonSx}>
                    {saving ? translate('devForm.saving') : translate('common.save')}
                </Button>
            </DialogActions>

            <BusinessProcessDialog
                open={!!processModal}
                mode={processModal?.mode ?? 'add'}
                data={processModal?.data}
                onClose={() => setProcessModal(null)}
                onConfirm={(params) => saveBusinessProcess(params, processModal?.mode ?? 'add', processModal?.data?.id)}
            />

            <DeleteConfirmDialog open={!!processDeleteId} onClose={() => setProcessDeleteId(null)} onConfirm={() => (processDeleteId ? deleteBusinessProcess(processDeleteId) : Promise.resolve(false))} testId="delete-process-dialog" />

            <DeleteConfirmDialog open={!!docDeleteId} onClose={() => setDocDeleteId(null)} onConfirm={() => (docDeleteId ? deleteDocument(docDeleteId) : Promise.resolve(false))} testId="delete-document-dialog" />

            <UploadDocumentDialog
                open={!!uploadModalCategory}
                categoryLabel={DOC_CATEGORIES.find((c) => c.key === uploadModalCategory)?.label}
                onClose={() => setUploadModalCategory(null)}
                onConfirm={(file) => (uploadModalCategory ? uploadDocument(uploadModalCategory, file) : Promise.resolve(false))}
            />
        </Dialog>
    );
}
